import { useMemo, useState, type FormEvent } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Droplets,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CubixApiError } from '@/lib/api/types';
import { cn, formatAmount } from '@/lib/utils';

import { useLcrReport } from './api';
import type { LcrNsfrReportPoint } from './types';

const COLOR_LCR  = 'hsl(221 83% 53%)'; // primary blue
const COLOR_NSFR = 'hsl(266 60% 55%)'; // muted purple

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function LcrNsfrReportPage() {
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => todayISO());
  const [submittedFrom, setSubmittedFrom] = useState(from);
  const [submittedTo, setSubmittedTo] = useState(to);

  const report = useLcrReport(submittedFrom, submittedTo);

  const chartData = useMemo(
    () =>
      (report.data?.points ?? []).map((p) => ({
        date: p.calculationDate,
        lcr: p.lcrRatio ? Number(p.lcrRatio) : null,
        nsfr: p.nsfrRatio ? Number(p.nsfrRatio) : null,
        lcrStr: p.lcrRatio,
        nsfrStr: p.nsfrRatio,
        lcrCompliant: p.lcrCompliant,
        nsfrCompliant: p.nsfrCompliant,
      })),
    [report.data?.points],
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmittedFrom(from);
    setSubmittedTo(to);
  }

  return (
    <div className="space-y-6">
      <header className="border-l-4 border-primary/60 pl-3">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Droplets className="h-5 w-5 text-primary" />
          Liquidity Coverage report (LCR / NSFR)
        </h1>
        <p className="text-sm text-muted-foreground">
          Basel III liquidity ratios over time. Compliance threshold is 100% (1.00).
        </p>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Date range</CardTitle>
          <CardDescription>One point per calculation date.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="lcr-from">From</Label>
              <Input
                id="lcr-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                required
                className="w-44"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lcr-to">To</Label>
              <Input
                id="lcr-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
                className="w-44"
              />
            </div>
            <Button type="submit" disabled={report.isFetching}>
              {report.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Run report
            </Button>
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFrom(isoDaysAgo(30));
                  setTo(todayISO());
                }}
              >
                Last 30d
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFrom(isoDaysAgo(90));
                  setTo(todayISO());
                }}
              >
                Last 90d
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFrom(isoDaysAgo(365));
                  setTo(todayISO());
                }}
              >
                Last 12m
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {report.isError ? <ApiErrorBanner error={report.error} /> : null}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <RatioTile
          title="LCR (avg)"
          ratio={report.data?.summary.lcrAvg}
          loading={report.isLoading}
        />
        <RatioTile
          title="NSFR (avg)"
          ratio={report.data?.summary.nsfrAvg}
          loading={report.isLoading}
        />
        <BreachTile
          title="LCR breaches"
          breaches={report.data?.summary.lcrBreaches ?? 0}
          total={report.data?.summary.pointCount ?? 0}
          loading={report.isLoading}
        />
        <BreachTile
          title="NSFR breaches"
          breaches={report.data?.summary.nsfrBreaches ?? 0}
          total={report.data?.summary.pointCount ?? 0}
          loading={report.isLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ratios over time</CardTitle>
          <CardDescription>
            Dashed line at 1.00 = Basel III minimum. Anything below is a breach.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {report.isLoading ? 'Loading…' : 'No LCR/NSFR data points in this range.'}
            </p>
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v as number).toFixed(2)}
                    domain={['auto', 'auto']}
                    width={60}
                  />
                  <ReferenceLine
                    y={1}
                    stroke="hsl(var(--destructive))"
                    strokeDasharray="4 3"
                    label={{
                      value: '100% (Basel III)',
                      position: 'insideTopRight',
                      fill: 'hsl(var(--destructive))',
                      fontSize: 11,
                    }}
                  />
                  <Tooltip content={<RatioTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Line
                    type="monotone"
                    dataKey="lcr"
                    name="LCR"
                    stroke={COLOR_LCR}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="nsfr"
                    name="NSFR"
                    stroke={COLOR_NSFR}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Calculations</CardTitle>
          <CardDescription>
            HQLA / net outflows breakdown for each calculation date.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <PointsTable points={report.data?.points ?? []} loading={report.isLoading} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tiles ──────────────────────────────────────────────────────────

function RatioTile({
  title,
  ratio,
  loading,
}: {
  title: string;
  ratio?: string;
  loading: boolean;
}) {
  const numeric = ratio ? Number(ratio) : null;
  const compliant = numeric !== null ? numeric >= 1 : null;
  const display = numeric !== null ? formatRatio(numeric) : '—';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle
          className={cn(
            'text-2xl',
            compliant === false ? 'text-destructive' : '',
          )}
        >
          {display}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-xs text-muted-foreground">
        {loading
          ? 'Loading…'
          : compliant === null
            ? 'No data'
            : compliant
              ? 'Above 100% threshold'
              : 'Below 100% threshold'}
      </CardContent>
    </Card>
  );
}

function BreachTile({
  title,
  breaches,
  total,
  loading,
}: {
  title: string;
  breaches: number;
  total: number;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle
          className={cn(
            'text-2xl',
            breaches > 0 ? 'text-destructive' : '',
          )}
        >
          {breaches}
          <span className="text-base font-normal text-muted-foreground">
            {' '}/ {total || '—'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-xs text-muted-foreground">
        {loading
          ? 'Loading…'
          : breaches === 0 && total > 0
            ? 'No breaches in range'
            : breaches > 0
              ? 'Days below 100% threshold'
              : 'No data'}
      </CardContent>
    </Card>
  );
}

// ── Tooltip + table ────────────────────────────────────────────────

function RatioTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload as {
    date: string;
    lcr: number | null;
    nsfr: number | null;
    lcrStr?: string;
    nsfrStr?: string;
    lcrCompliant?: boolean;
    nsfrCompliant?: boolean;
  };
  return (
    <div className="rounded-md border bg-popover p-3 text-xs shadow-md">
      <p className="mb-1.5 text-sm font-medium">{d.date}</p>
      <Row label="LCR" raw={d.lcrStr} compliant={d.lcrCompliant} />
      <Row label="NSFR" raw={d.nsfrStr} compliant={d.nsfrCompliant} />
    </div>
  );
}

function Row({
  label,
  raw,
  compliant,
}: {
  label: string;
  raw?: string;
  compliant?: boolean;
}) {
  if (!raw) return null;
  const num = Number(raw);
  return (
    <div className="flex items-baseline justify-between gap-6">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          'tabular-nums font-medium',
          compliant === false ? 'text-destructive' : 'text-foreground',
        )}
      >
        {formatRatio(num)}
      </span>
    </div>
  );
}

function PointsTable({
  points,
  loading,
}: {
  points: LcrNsfrReportPoint[];
  loading: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Date</th>
            <th className="px-4 py-2 text-right font-medium">LCR</th>
            <th className="px-4 py-2 text-right font-medium">HQLA</th>
            <th className="px-4 py-2 text-right font-medium">Net outflows</th>
            <th className="px-4 py-2 text-right font-medium">NSFR</th>
            <th className="px-4 py-2 text-left font-medium">Compliance</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
              </td>
            </tr>
          ) : points.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                No calculations in this range.
              </td>
            </tr>
          ) : (
            points.map((p) => (
              <tr key={p.calculationDate} className="border-b last:border-0">
                <td className="px-4 py-2">{p.calculationDate}</td>
                <td
                  className={cn(
                    'px-4 py-2 text-right tabular-nums',
                    p.lcrCompliant === false ? 'text-destructive' : '',
                  )}
                >
                  {p.lcrRatio ? formatRatio(Number(p.lcrRatio)) : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                  {p.lcrHqla ? formatAmount(p.lcrHqla) : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                  {p.lcrNetOutflows ? formatAmount(p.lcrNetOutflows) : '—'}
                </td>
                <td
                  className={cn(
                    'px-4 py-2 text-right tabular-nums',
                    p.nsfrCompliant === false ? 'text-destructive' : '',
                  )}
                >
                  {p.nsfrRatio ? formatRatio(Number(p.nsfrRatio)) : '—'}
                </td>
                <td className="px-4 py-2">
                  <ComplianceBadge
                    lcrCompliant={p.lcrCompliant}
                    nsfrCompliant={p.nsfrCompliant}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function ComplianceBadge({
  lcrCompliant,
  nsfrCompliant,
}: {
  lcrCompliant?: boolean;
  nsfrCompliant?: boolean;
}) {
  const bothPass = lcrCompliant !== false && nsfrCompliant !== false;
  if (bothPass) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Compliant
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] font-medium text-destructive">
      <AlertTriangle className="h-3 w-3" />
      {lcrCompliant === false && nsfrCompliant === false
        ? 'LCR + NSFR breach'
        : lcrCompliant === false
          ? 'LCR breach'
          : 'NSFR breach'}
    </span>
  );
}

function formatRatio(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
          </p>
        ) : null}
      </div>
    </div>
  );
}
